// The outbox on disk (M29, D66): IndexedDB, so the service worker can send
// what's waiting while Twinden is closed (public/sw.js reads the same
// database). Every function fails soft: the in-memory outbox keeps working.
import type { OutboxEntry } from '../store';

const DB_NAME = 'twinden-outbox';
const ENTRIES = 'entries';
const META = 'meta';

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (opening) return opening;
  opening = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('No IndexedDB'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      const entries = db.createObjectStore(ENTRIES, { keyPath: 'clientId' });
      entries.createIndex('ownerId', 'ownerId');
      db.createObjectStore(META);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  opening.catch(() => {
    opening = null;
  });
  return opening;
}

function finished(tx: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function readEntries(ownerId: string): Promise<OutboxEntry[]> {
  try {
    const db = await open();
    const tx = db.transaction(ENTRIES, 'readonly');
    const request = tx.objectStore(ENTRIES).index('ownerId').getAll(ownerId);
    await finished(tx);
    return (request.result as OutboxEntry[]).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    );
  } catch {
    return [];
  }
}

/** Makes the stored outbox for this user exactly `entries`. */
export async function writeEntries(ownerId: string, entries: OutboxEntry[]) {
  try {
    const db = await open();
    const tx = db.transaction(ENTRIES, 'readwrite');
    const store = tx.objectStore(ENTRIES);
    const keep = new Set(entries.map((entry) => entry.clientId));
    const existing = store.index('ownerId').getAllKeys(ownerId);
    existing.onsuccess = () => {
      for (const key of existing.result) {
        if (!keep.has(String(key))) store.delete(key);
      }
    };
    for (const entry of entries) store.put({ ...entry, ownerId });
    await finished(tx);
  } catch {
    // Not persisted; still in memory.
  }
}

/** What the service worker needs to send for this user (D68). */
export async function writeAuth(
  auth: { userId: string; accessToken: string } | null
) {
  try {
    const db = await open();
    const tx = db.transaction(META, 'readwrite');
    if (auth) tx.objectStore(META).put(auth, 'auth');
    else tx.objectStore(META).delete('auth');
    await finished(tx);
  } catch {
    // Background sending just won't happen.
  }
}

/** Asks the browser to wake the service worker when the connection returns. */
export function requestBackgroundSync() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator))
    return;
  void navigator.serviceWorker.ready
    .then((registration) =>
      (
        registration as ServiceWorkerRegistration & {
          sync?: { register: (tag: string) => Promise<void> };
        }
      ).sync?.register('twinden-outbox')
    )
    .catch(() => undefined);
}
